import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

const DEVICE_ID_KEY = "cinerig_device_id";
const SESSION_ID_KEY = "cinerig_session_id";
const FIRST_TOUCH_UTM_KEY = "cinerig_first_touch_utm";
const GA_CLIENT_ID_KEY = "cinerig_ga_client_id";

/** Strip sensitive query params (__lovable_token, etc.) from paths and referrers */
function sanitizePath(raw: string): string {
  try {
    // Handle full URLs
    if (raw.startsWith('http')) {
      const url = new URL(raw);
      url.searchParams.delete('__lovable_token');
      // Return just origin + pathname + cleaned search
      return url.pathname + url.search;
    }
    // Handle relative paths
    const fakeBase = 'https://x.com';
    const url = new URL(raw, fakeBase);
    url.searchParams.delete('__lovable_token');
    return url.pathname + url.search;
  } catch {
    return raw;
  }
}

function sanitizeReferrer(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    url.searchParams.delete('__lovable_token');
    return url.toString();
  } catch {
    return raw;
  }
}

export type ProductEventName =
  | "page_view"
  | "rig_created"
  | "rig_deleted"
  | "camera_selected"
  | "accessory_added"
  | "accessory_removed"
  | "pdf_exported"
  | "user_registered"
  | "user_logged_in"
  | "project_created"
  | "project_deleted"
  | "rig_shared"
  | "guided_builder_started"
  | "guided_builder_completed";

type UTMFields = {
  referrer?: string | null;
  landing_path?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
};

function safeGetStorageItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetStorageItem(storage: Storage, key: string, value: string) {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function getOrCreateDeviceId(): string {
  const existing = safeGetStorageItem(localStorage, DEVICE_ID_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  safeSetStorageItem(localStorage, DEVICE_ID_KEY, id);
  return id;
}

export function getOrCreateSessionId(opts?: { rotate?: boolean }): string {
  if (!opts?.rotate) {
    const existing = safeGetStorageItem(sessionStorage, SESSION_ID_KEY);
    if (existing) return existing;
  }
  const id = crypto.randomUUID();
  safeSetStorageItem(sessionStorage, SESSION_ID_KEY, id);
  return id;
}

function parseUtmFromUrl(url: URL): UTMFields {
  const sp = url.searchParams;
  return {
    utm_source: sp.get("utm_source"),
    utm_medium: sp.get("utm_medium"),
    utm_campaign: sp.get("utm_campaign"),
    utm_term: sp.get("utm_term"),
    utm_content: sp.get("utm_content"),
  };
}

export function getFirstTouchAttribution(currentPath: string): UTMFields {
  const existingRaw = safeGetStorageItem(localStorage, FIRST_TOUCH_UTM_KEY);
  if (existingRaw) {
    try {
      return JSON.parse(existingRaw) as UTMFields;
    } catch {
      // fall through
    }
  }

  const url = new URL(window.location.href);
  const utm = parseUtmFromUrl(url);
  const attribution: UTMFields = {
    ...utm,
    referrer: document.referrer || null,
    landing_path: currentPath,
  };

  const hasAny =
    !!attribution.utm_source ||
    !!attribution.utm_medium ||
    !!attribution.utm_campaign ||
    !!attribution.utm_term ||
    !!attribution.utm_content ||
    !!attribution.referrer;

  if (hasAny) {
    safeSetStorageItem(localStorage, FIRST_TOUCH_UTM_KEY, JSON.stringify(attribution));
  }

  return attribution;
}

async function ensureSessionRow(params: {
  sessionId: string;
  deviceId: string;
  userId: string | null;
  path: string;
  attribution: UTMFields;
}) {
  const { sessionId, deviceId, userId, path, attribution } = params;

  const gaClientId = safeGetStorageItem(localStorage, GA_CLIENT_ID_KEY);
  const { error } = await supabase.from("analytics_sessions").insert(
      {
        id: sessionId,
        device_id: deviceId,
        user_id: userId,
        ga_client_id: gaClientId,
        landing_path: sanitizePath(attribution.landing_path ?? path),
        referrer: attribution.referrer ?? null,
        utm_source: attribution.utm_source ?? null,
        utm_medium: attribution.utm_medium ?? null,
        utm_campaign: attribution.utm_campaign ?? null,
        utm_term: attribution.utm_term ?? null,
        utm_content: attribution.utm_content ?? null,
        user_agent: navigator.userAgent,
      }
  );

  if (error && error.code !== '23505') {
    // 23505 = unique_violation (session already exists) — expected and safe to ignore
    if (!import.meta.env.PROD) console.warn("[Analytics] session insert failed", error);
  }
}

export async function trackProductEvent(
  eventName: ProductEventName,
  params?: Record<string, unknown>,
  opts?: { userId?: string | null; path?: string }
) {
  try {
    const deviceId = getOrCreateDeviceId();
    const sessionId = getOrCreateSessionId();
    const rawPath = opts?.path ?? window.location.pathname + window.location.search;
    const path = sanitizePath(rawPath);
    const userId = opts?.userId ?? null;
    const attribution = getFirstTouchAttribution(path);

    // Sanitize attribution referrer
    const cleanReferrer = sanitizeReferrer(attribution.referrer ?? null);

    // Await session insert so FK constraint is satisfied before event insert
    await ensureSessionRow({ sessionId, deviceId, userId, path, attribution: { ...attribution, referrer: cleanReferrer } });

    const gaClientId = safeGetStorageItem(localStorage, GA_CLIENT_ID_KEY);

    const { error } = await supabase.from("analytics_events").insert({
      event_name: eventName,
      session_id: sessionId,
      device_id: deviceId,
      user_id: userId,
      ga_client_id: gaClientId,
      path,
      properties: (params ?? {}) as Json,
      referrer: cleanReferrer,
      utm_source: attribution.utm_source ?? null,
      utm_medium: attribution.utm_medium ?? null,
      utm_campaign: attribution.utm_campaign ?? null,
      utm_term: attribution.utm_term ?? null,
      utm_content: attribution.utm_content ?? null,
    });

    if (error) {
      if (!import.meta.env.PROD) console.warn("[Analytics] event insert failed", error);
    }
  } catch {
    // Analytics must never break UX
  }
}

export function rotateSessionOnAuth(userId: string | null) {
  // Create a fresh session boundary when auth state changes
  getOrCreateSessionId({ rotate: true });
}
